/**
 * ProjectReport Component - Redesigned
 * 
 * Professional dashboard-style project reports with clean UI inspired by ProHub.
 * Features comprehensive analytics, workflow tracking, resource management, and time logging.
 * 
 * @param {string} projectId - The ID of the project
 * @param {string} projectName - The name of the project
 */

import { useState } from 'react';
import { Row, Col, Button, Badge, Table, Accordion, Card, ProgressBar } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { 
    ExportReportModal, 
    ViewTaskDetailsModal, 
    ViewResourceDetailsModal,
    ViewTimeLogDetailsModal 
} from './ProjectReportsModals';
import FilterProjectReportsOffcanvas from './ProjectReportsFilter';
import './ProjectReports.css';

const ProjectReport = ({ projectId, projectName }) => {
    // Modal & Filter States
    const [showFilter, setShowFilter] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [showTimeLogModal, setShowTimeLogModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Filter States
    const [teamMemberFilter, setTeamMemberFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [workflowFilter, setWorkflowFilter] = useState([]);
    const [dateRangeFilter, setDateRangeFilter] = useState([null, null]);

    // Filter Options
    const teamMemberOptions = [
        { value: 'john', label: 'John Doe' },
        { value: 'jane', label: 'Jane Smith' },
        { value: 'mike', label: 'Mike Johnson' },
        { value: 'sarah', label: 'Sarah Wilson' },
        { value: 'david', label: 'David Brown' },
        { value: 'emily', label: 'Emily Chen' },
        { value: 'robert', label: 'Robert Taylor' },
        { value: 'lisa', label: 'Lisa Anderson' }
    ];

    const statusOptions = [
        { value: 'backlog', label: 'Backlog' },
        { value: 'planning', label: 'Planning' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'on-hold', label: 'On Hold' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const priorityOptions = [
        { value: 'high', label: 'High Priority' },
        { value: 'medium', label: 'Medium Priority' },
        { value: 'low', label: 'Low Priority' }
    ];

    const workflowOptions = [
        { value: 'general', label: 'General Workflow' },
        { value: 'tech', label: 'Tech Team Workflow' },
        { value: 'uiux', label: 'UI/UX Workflow' },
        { value: 'programming', label: 'Programming Workflow' },
        { value: 'qc', label: 'QC Workflow' },
        { value: 'design', label: 'Design Workflow' }
    ];

    // Pagination States for Team Performance
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // ============================================
    // HELPER FUNCTIONS
    // ============================================
    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('progress') || statusLower.includes('active')) return 'primary';
        if (statusLower.includes('complete') || statusLower.includes('done')) return 'success';
        if (statusLower.includes('pending') || statusLower.includes('review')) return 'warning';
        if (statusLower.includes('blocked') || statusLower.includes('hold')) return 'danger';
        return 'secondary';
    };

    const getPriorityColor = (priority) => {
        const priorityLower = priority?.toLowerCase() || '';
        if (priorityLower.includes('high') || priorityLower.includes('critical')) return 'danger';
        if (priorityLower.includes('medium') || priorityLower.includes('normal')) return 'warning';
        if (priorityLower.includes('low')) return 'primary';
        return 'secondary';
    };

    const getUrgencyColor = (urgency) => {
        const colors = {
            'high': 'danger',
            'medium': 'warning',
            'low': 'info'
        };
        return colors[urgency] || 'secondary';
    };

    // ============================================
    // 1. PROJECT OVERVIEW DATA
    // ============================================
    const projectOverview = {
        // Project Basic Info
        projectName: projectName || 'eCommerce Website Redesign',
        startDate: 'Jan 15, 2025',
        endDate: 'Mar 30, 2025',
        duration: '75 Days',
        projectManager: 'John Doe',
        lastUpdated: '2 hours ago',
        progress: 57,
        category: 'Agile Project',
        status: 'In Progress',
        priority: 'High',
        createdBy: 'John Doe',
        createdOn: '13 Aug 2024',
        estimatedHours: 1500,
        
        // Task Metrics
        totalTasks: 156,
        completedTasks: 89,
        inProgress: 42,
        overdueTasks: 12,
        totalHoursLogged: 1248,
        teamMembers: 12,
        activeTaskLists: 6,
        upcomingDeadlines: 8
    };

    // Task Stats for Overview Cards
    const taskStats = {
        total: projectOverview.totalTasks,
        completed: projectOverview.completedTasks,
        inProgress: projectOverview.inProgress,
        overdue: projectOverview.overdueTasks
    };

    // ============================================
    // 2. WORKFLOW DATA
    // ============================================
    const workflowChartData = {
        labels: ['General', 'Tech Team', 'UI/UX', 'Programming', 'QC', 'Design'],
        datasets: [
            { label: 'Completed', data: [35, 28, 22, 31, 19, 24], color: '#10b981' },
            { label: 'In Progress', data: [15, 12, 8, 14, 7, 10], color: '#3b82f6' },
            { label: 'Pending', data: [8, 10, 5, 9, 4, 6], color: '#f59e0b' },
            { label: 'Blocked', data: [2, 3, 1, 2, 1, 2], color: '#ef4444' }
        ]
    };

    const workflows = [
        {
            name: 'General Workflow',
            stages: [
                { stage: 'Backlog', taskCount: 12, completed: 8, inProgress: 3, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Planning', taskCount: 15, completed: 10, inProgress: 4, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'In Progress', taskCount: 18, completed: 5, inProgress: 10, pending: 2, blocked: 1, overdue: 0 },
                { stage: 'Review', taskCount: 10, completed: 6, inProgress: 3, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Completed', taskCount: 35, completed: 35, inProgress: 0, pending: 0, blocked: 0, overdue: 0 }
            ]
        },
        {
            name: 'Tech Team Workflow',
            stages: [
                { stage: 'Requirements', taskCount: 8, completed: 6, inProgress: 2, pending: 0, blocked: 0, overdue: 0 },
                { stage: 'Development', taskCount: 20, completed: 12, inProgress: 6, pending: 2, blocked: 0, overdue: 0 },
                { stage: 'Code Review', taskCount: 12, completed: 8, inProgress: 3, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Testing', taskCount: 15, completed: 10, inProgress: 4, pending: 1, blocked: 0, overdue: 0 }
            ]
        },
        {
            name: 'UI/UX Workflow',
            stages: [
                { stage: 'Research', taskCount: 6, completed: 5, inProgress: 1, pending: 0, blocked: 0, overdue: 0 },
                { stage: 'Wireframing', taskCount: 10, completed: 7, inProgress: 2, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Design', taskCount: 14, completed: 9, inProgress: 4, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Prototype', taskCount: 8, completed: 6, inProgress: 2, pending: 0, blocked: 0, overdue: 0 }
            ]
        },
        {
            name: 'Programming Workflow',
            stages: [
                { stage: 'Setup', taskCount: 5, completed: 5, inProgress: 0, pending: 0, blocked: 0, overdue: 0 },
                { stage: 'Coding', taskCount: 25, completed: 15, inProgress: 8, pending: 2, blocked: 0, overdue: 0 },
                { stage: 'Unit Testing', taskCount: 18, completed: 10, inProgress: 6, pending: 2, blocked: 0, overdue: 0 },
                { stage: 'Integration', taskCount: 12, completed: 8, inProgress: 3, pending: 1, blocked: 0, overdue: 0 }
            ]
        },
        {
            name: 'QC Workflow',
            stages: [
                { stage: 'Test Planning', taskCount: 7, completed: 5, inProgress: 2, pending: 0, blocked: 0, overdue: 0 },
                { stage: 'Execution', taskCount: 15, completed: 9, inProgress: 5, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Bug Reporting', taskCount: 10, completed: 7, inProgress: 2, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Regression', taskCount: 8, completed: 5, inProgress: 2, pending: 1, blocked: 0, overdue: 0 }
            ]
        },
        {
            name: 'Design Workflow',
            stages: [
                { stage: 'Concept', taskCount: 9, completed: 7, inProgress: 2, pending: 0, blocked: 0, overdue: 0 },
                { stage: 'Visual Design', taskCount: 16, completed: 11, inProgress: 4, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Review', taskCount: 11, completed: 8, inProgress: 2, pending: 1, blocked: 0, overdue: 0 },
                { stage: 'Finalization', taskCount: 7, completed: 5, inProgress: 2, pending: 0, blocked: 0, overdue: 0 }
            ]
        }
    ];

    // ============================================
    // 3. TASK DISTRIBUTION DATA
    // ============================================
    const taskDistribution = {
        byStatus: [
            { label: 'Completed', count: 89, color: '#10b981', icon: 'fa-check-circle' },
            { label: 'In Progress', count: 42, color: '#3b82f6', icon: 'fa-spinner' },
            { label: 'Pending', count: 13, color: '#f59e0b', icon: 'fa-clock' },
            { label: 'Blocked', count: 12, color: '#ef4444', icon: 'fa-ban' }
        ],
        byPriority: [
            { label: 'High', count: 45, color: '#ef4444', icon: 'fa-fire' },
            { label: 'Medium', count: 68, color: '#f59e0b', icon: 'fa-exclamation-circle' },
            { label: 'Low', count: 43, color: '#3b82f6', icon: 'fa-arrow-down' }
        ],
        byDepartment: [
            { label: 'Development', count: 52, color: '#3b82f6' },
            { label: 'Design', count: 38, color: '#8b5cf6' },
            { label: 'Testing', count: 34, color: '#10b981' },
            { label: 'Planning', count: 32, color: '#f59e0b' }
        ],
        byAssignee: [
            { name: 'John Doe', count: 18, avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
            { name: 'Jane Smith', count: 16, avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
            { name: 'Mike Johnson', count: 15, avatar: 'https://randomuser.me/api/portraits/men/45.jpg' },
            { name: 'Sarah Wilson', count: 14, avatar: 'https://randomuser.me/api/portraits/women/50.jpg' }
        ]
    };

    const taskByStatus = [
        { status: 'Backlog', count: 8, color: '#6b7280' },
        { status: 'Planning', count: 6, color: '#8b5cf6' },
        { status: 'In Progress', count: 12, color: '#3b82f6' },
        { status: 'Completed', count: 18, color: '#10b981' },
        { status: 'On Hold', count: 3, color: '#f59e0b' },
        { status: 'Cancelled', count: 1, color: '#ef4444' }
    ];

    const upcomingDeadlines = [
        { id: 1, task: 'Homepage Design Completion', dueDate: 'Dec 5, 2024', priority: 'High', assignedTo: 'Sarah Wilson', status: 'In Progress', urgency: 'high', daysLeft: 1 },
        { id: 2, task: 'API Integration Testing', dueDate: 'Dec 8, 2024', priority: 'Medium', assignedTo: 'Mike Johnson', status: 'Pending', urgency: 'medium', daysLeft: 4 },
        { id: 3, task: 'Mobile Responsive Design', dueDate: 'Dec 12, 2024', priority: 'Low', assignedTo: 'Jane Smith', status: 'In Progress', urgency: 'low', daysLeft: 8 },
        { id: 4, task: 'Database Optimization', dueDate: 'Dec 15, 2024', priority: 'High', assignedTo: 'David Brown', status: 'Pending', urgency: 'high', daysLeft: 11 },
        { id: 5, task: 'User Authentication Module', dueDate: 'Dec 18, 2024', priority: 'High', assignedTo: 'John Doe', status: 'In Progress', urgency: 'medium', daysLeft: 14 }
    ];

    // ============================================
    // 4. TASKS BY RESOURCE DATA
    // ============================================
    const resources = [
        {
            id: 1,
            name: 'John Doe',
            avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
            initials: 'JD',
            role: 'Senior Developer',
            assignedTasks: 18,
            completedTasks: 12,
            inProgressTasks: 5,
            overdueTasks: 1,
            totalHoursLogged: 142
        },
        {
            id: 2,
            name: 'Jane Smith',
            avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
            initials: 'JS',
            role: 'UI/UX Designer',
            assignedTasks: 16,
            completedTasks: 11,
            inProgressTasks: 4,
            overdueTasks: 1,
            totalHoursLogged: 128
        },
        {
            id: 3,
            name: 'Mike Johnson',
            avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
            initials: 'MJ',
            role: 'Full Stack Developer',
            assignedTasks: 15,
            completedTasks: 10,
            inProgressTasks: 4,
            overdueTasks: 1,
            totalHoursLogged: 135
        },
        {
            id: 4,
            name: 'Sarah Wilson',
            avatar: 'https://randomuser.me/api/portraits/women/50.jpg',
            initials: 'SW',
            role: 'QA Engineer',
            assignedTasks: 14,
            completedTasks: 10,
            inProgressTasks: 3,
            overdueTasks: 1,
            totalHoursLogged: 118
        },
        {
            id: 5,
            name: 'David Brown',
            avatar: 'https://randomuser.me/api/portraits/men/52.jpg',
            initials: 'DB',
            role: 'Backend Developer',
            assignedTasks: 13,
            completedTasks: 9,
            inProgressTasks: 3,
            overdueTasks: 1,
            totalHoursLogged: 125
        },
        {
            id: 6,
            name: 'Emily Davis',
            avatar: 'https://randomuser.me/api/portraits/women/65.jpg',
            initials: 'ED',
            role: 'Project Manager',
            assignedTasks: 12,
            completedTasks: 8,
            inProgressTasks: 3,
            overdueTasks: 1,
            totalHoursLogged: 98
        }
    ];

    // ============================================
    // 5. TEAM PERFORMANCE DATA
    // ============================================
    const teamPerformance = [
        { id: 1, name: 'John Doe', role: 'Project Manager', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', initials: 'JD', tasksAssigned: 12, tasksCompleted: 8, tasksPending: 4, efficiency: 85 },
        { id: 2, name: 'Jane Smith', role: 'Team Lead', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', initials: 'JS', tasksAssigned: 15, tasksCompleted: 10, tasksPending: 5, efficiency: 78 },
        { id: 3, name: 'Mike Johnson', role: 'Developer', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', initials: 'MJ', tasksAssigned: 10, tasksCompleted: 7, tasksPending: 3, efficiency: 92 },
        { id: 4, name: 'Sarah Wilson', role: 'Designer', avatar: 'https://randomuser.me/api/portraits/women/50.jpg', initials: 'SW', tasksAssigned: 8, tasksCompleted: 6, tasksPending: 2, efficiency: 88 },
        { id: 5, name: 'David Brown', role: 'QA Engineer', avatar: 'https://randomuser.me/api/portraits/men/52.jpg', initials: 'DB', tasksAssigned: 11, tasksCompleted: 9, tasksPending: 2, efficiency: 95 },
        { id: 6, name: 'Emily Chen', role: 'Frontend Developer', avatar: 'https://randomuser.me/api/portraits/women/68.jpg', initials: 'EC', tasksAssigned: 13, tasksCompleted: 10, tasksPending: 3, efficiency: 89 },
        { id: 7, name: 'Robert Taylor', role: 'Backend Developer', avatar: 'https://randomuser.me/api/portraits/men/75.jpg', initials: 'RT', tasksAssigned: 14, tasksCompleted: 11, tasksPending: 3, efficiency: 91 },
        { id: 8, name: 'Lisa Anderson', role: 'DevOps Engineer', avatar: 'https://randomuser.me/api/portraits/women/82.jpg', initials: 'LA', tasksAssigned: 9, tasksCompleted: 7, tasksPending: 2, efficiency: 87 }
    ];

    const milestones = [
        { name: 'Project Kickoff', start: 5, duration: 100, color: '#10b981' },
        { name: 'Design Phase', start: 15, duration: 85, color: '#3b82f6' },
        { name: 'Development Phase', start: 35, duration: 65, color: '#f59e0b' },
        { name: 'Testing Phase', start: 65, duration: 40, color: '#8b5cf6' },
        { name: 'Deployment', start: 85, duration: 15, color: '#ef4444' }
    ];

    // ============================================
    // 6. TIME LOGGED DATA (MERGED)
    // ============================================
    const timeLogData = [
        {
            id: 1,
            resourceName: 'John Doe',
            taskName: 'API Integration',
            date: 'Dec 7, 2025',
            startTime: '09:00 AM',
            endTime: '12:30 PM',
            totalHours: '3.5h',
            notes: 'Completed REST API endpoints',
            billable: true,
            status: 'Approved'
        },
        {
            id: 2,
            resourceName: 'Jane Smith',
            taskName: 'Homepage Design',
            date: 'Dec 7, 2025',
            startTime: '10:00 AM',
            endTime: '01:00 PM',
            totalHours: '3h',
            notes: 'Finalized mockups',
            billable: true,
            status: 'Approved'
        },
        {
            id: 3,
            resourceName: 'Mike Johnson',
            taskName: 'Database Setup',
            date: 'Dec 6, 2025',
            startTime: '02:00 PM',
            endTime: '06:00 PM',
            totalHours: '4h',
            notes: 'Configured PostgreSQL',
            billable: false,
            status: 'Pending'
        },
        {
            id: 4,
            resourceName: 'Sarah Wilson',
            taskName: 'Test Case Writing',
            date: 'Dec 6, 2025',
            startTime: '09:30 AM',
            endTime: '12:00 PM',
            totalHours: '2.5h',
            notes: 'Created test scenarios',
            billable: true,
            status: 'Approved'
        },
        {
            id: 5,
            resourceName: 'David Brown',
            taskName: 'Backend API Development',
            date: 'Dec 5, 2025',
            startTime: '11:00 AM',
            endTime: '04:30 PM',
            totalHours: '5.5h',
            notes: 'Implemented authentication',
            billable: true,
            status: 'Approved'
        }
    ];

    // ============================================
    // HANDLER FUNCTIONS
    // ============================================
    const handleViewTask = (task) => {
        setSelectedItem(task);
        setShowTaskModal(true);
    };

    const handleViewTaskDetails = (task) => {
        setSelectedItem(task);
        setShowTaskModal(true);
    };

    const handleViewResource = (resource) => {
        setSelectedItem(resource);
        setShowResourceModal(true);
    };

    const handleViewTimeLog = (log) => {
        setSelectedItem(log);
        setShowTimeLogModal(true);
    };

    const handleViewMemberDetails = (member) => {
        setSelectedItem(member);
        setShowResourceModal(true);
    };

    const handleExport = (format) => {
        console.log(`Exporting report as ${format}`);
    };

    return (
        <>
            {/* ============================================ */}
            {/* PROJECT INFORMATION SECTION */}
            {/* ============================================ */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <div className="fw-bold">Project Overview</div>
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            <div className="d-flex gap-2 me-3">
                                <span className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getStatusColor(projectOverview.status)}`}>
                                    {projectOverview.status}
                                </span>
                                <span className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getPriorityColor(projectOverview.priority)}`}>
                                    {projectOverview.priority} Priority
                                </span>
                            </div>
                            <Button 
                                variant='outline-primary' 
                                className='btn-sm text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                            <Button 
                                variant='primary'
                                className='btn-sm text-nowrap'
                                onClick={() => setShowExportModal(true)}
                            >
                                <i className="fas fa-download me-1"></i>
                                Export
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1'>
                    <Row className="g-4">
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Project Name
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.projectName}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Start Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.startDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                End Date
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.endDate}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Duration
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.duration}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Project Manager
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.projectManager}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Last Updated
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.lastUpdated}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Progress
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.progress}%
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Completion
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.completedTasks}/{projectOverview.totalTasks} Tasks
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Category
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.category}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Created By
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.createdBy}
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-uppercase text-secondary fw-medium mb-1 fs-11">
                                Created On
                            </div>
                            <div className="text-dark fw-semibold fs-15">
                                {projectOverview.createdOn}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* ============================================ */}
            {/* 1. PROJECT OVERVIEW CARDS */}
            {/* ============================================ */}
            <Row className="g-4 mb-4">
                {/* 1. Project Progress % */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 text-info rounded-3 icon-box-48">
                                <i className="fas fa-chart-line"></i>
                            </div>
                            <span className="fw-semibold small text-info-custom">Status</span>
                        </div>
                        <div className="text-info fw-bold mb-1 fs-28">
                            {Math.round((taskStats.completed / taskStats.total) * 100)}%
                        </div>
                        <div className="text-secondary small mb-2">Project Progress</div>
                        <ProgressBar 
                            now={(taskStats.completed / taskStats.total) * 100}
                            className="rounded-pill progress-sm"
                            variant="info"
                        />
                    </div>
                </Col>

                {/* 2. Time Tracking Summary */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-3 icon-box-48">
                                <i className="fas fa-clock"></i>
                            </div>
                            <span className="fw-semibold small text-success-custom">On Track</span>
                        </div>
                        <div className="text-primary fw-bold mb-1 fs-28">485h</div>
                        <div className="text-secondary small mb-2">Total Logged Hours</div>
                        <div className="d-flex justify-content-between small">
                            <span className="text-muted">Est: 620h</span>
                            <span className="text-success fw-semibold">135h left</span>
                        </div>
                    </div>
                </Col>

                {/* 3. High Priority Tasks */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger rounded-3 icon-box-48">
                                <i className="fas fa-fire"></i>
                            </div>
                            <span className="fw-semibold small text-danger-custom">Urgent</span>
                        </div>
                        <div className="text-danger fw-bold mb-1 fs-28">15</div>
                        <div className="text-secondary small mb-2">High Priority Tasks</div>
                        <div className="d-flex gap-2">
                            <span className="fw-semibold fs-14 text-danger-custom">9 Overdue</span>
                            <span className="fw-semibold fs-14 text-warning-custom">6 Due Soon</span>
                        </div>
                    </div>
                </Col>

                {/* 4. Team Workload */}
                <Col lg={3} md={6}>
                    <div className="bg-white border rounded-3 p-3 h-100 summary-stat-card-hover">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success rounded-3 icon-box-48">
                                <i className="fas fa-users"></i>
                            </div>
                            <span className="fw-semibold small text-secondary-custom">5 Members</span>
                        </div>
                        <div className="text-success fw-bold mb-1 fs-28">13</div>
                        <div className="text-secondary small mb-2">Avg Tasks/Member</div>
                        <div className="small">
                            <span className="text-muted">Most Active: </span>
                            <span className="fw-semibold text-dark">Mike Johnson</span>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ============================================ */}
            {/* 2. TASKS BY WORKFLOW CHART */}
            {/* ============================================ */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div>
                        <div className="fw-bold">Tasks by Workflow</div>
                    </div>
                </div>
                <div className='c-card-body-1'>
                    <Chart 
                        options={{
                            chart: {
                                type: 'bar',
                                height: 400,
                                toolbar: {
                                    show: false
                                },
                                background: 'transparent',
                                foreColor: '#333'
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                    columnWidth: '55%',
                                    endingShape: 'rounded',
                                    borderRadius: 4,
                                },
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            stroke: {
                                show: true,
                                width: 2,
                                colors: ['transparent']
                            },
                            xaxis: {
                                categories: workflowChartData.labels,
                                axisBorder: {
                                    show: false
                                },
                                axisTicks: {
                                    show: false
                                },
                                labels: {
                                    style: {
                                        colors: '#666',
                                        fontSize: '12px',
                                        fontWeight: 600
                                    }
                                }
                            },
                            yaxis: {
                                min: 0,
                                labels: {
                                    formatter: function (val) {
                                        return Math.round(val) + ' tasks';
                                    },
                                    style: {
                                        colors: '#666',
                                        fontSize: '12px'
                                    }
                                }
                            },
                            fill: {
                                opacity: 1,
                                type: 'gradient',
                                gradient: {
                                    shade: 'light',
                                    type: 'vertical',
                                    shadeIntensity: 0.3,
                                    gradientToColors: ['#667eea'],
                                    inverseColors: false,
                                    opacityFrom: 0.8,
                                    opacityTo: 0.6,
                                    stops: [0, 100]
                                }
                            },
                            tooltip: {
                                y: {
                                    formatter: function (val) {
                                        return val + ' tasks';
                                    }
                                },
                                theme: 'light'
                            },
                            grid: {
                                borderColor: '#e0e6ed',
                                strokeDashArray: 5,
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: true
                                    }
                                },
                                padding: {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0
                                }
                            },
                            colors: ['#764ba2'],
                            legend: {
                                show: false
                            }
                        }} 
                        series={[{
                            name: 'Total Tasks',
                            data: workflows.map(workflow => 
                                workflow.stages.reduce((sum, stage) => sum + stage.taskCount, 0)
                            )
                        }]} 
                        type="bar" 
                        height={400} 
                    />
                </div>
            </div>

            {/* ============================================ */}
            {/* 3. WORKFLOW STAGE DETAILS */}
            {/* ============================================ */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div>
                        <div className="fw-bold">Workflow Stage Details</div>
                    </div>
                </div>
                <div className='c-card-body-1 p-0'>
                    <Accordion defaultActiveKey="0" flush>
                        {workflows.map((workflow, index) => (
                            <Accordion.Item eventKey={index.toString()} key={index}>
                                <Accordion.Header>
                                    <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                                        <span className="fw-semibold">{workflow.name}</span>
                                        <span className="text-muted small fw-semibold">{workflow.stages.length} Stages</span>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    <Table responsive className="c-table mb-0 border-top">
                                        <thead>
                                            <tr>
                                                <th>Stage</th>
                                                <th className="text-center">Task Count</th>
                                                <th className="text-center">Completed</th>
                                                <th className="text-center">In Progress</th>
                                                <th className="text-center">Pending</th>
                                                <th className="text-center">Blocked</th>
                                                <th className="text-center">Overdue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {workflow.stages.map((stage, stageIndex) => (
                                                <tr key={stageIndex}>
                                                    <td>
                                                        <span className="fw-semibold">{stage.stage}</span>
                                                    </td>
                                                    <td className="text-center">{stage.taskCount}</td>
                                                    <td className="text-center">{stage.completed}</td>
                                                    <td className="text-center">{stage.inProgress}</td>
                                                    <td className="text-center">{stage.pending}</td>
                                                    <td className="text-center">{stage.blocked}</td>
                                                    <td className="text-center">{stage.overdue}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            </div>

            {/* ============================================ */}
            {/* 4. TASK DISTRIBUTION */}
            {/* ============================================ */}
            <Row className="g-4 mb-4">
                <Col lg={8}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <span>Tasks by Status</span>
                        </div>
                        <div className='c-card-body-1'>
                            <Row className="g-3">
                                {taskByStatus.map((stat, index) => {
                                    const statusIcons = {
                                        'Backlog': 'fas fa-inbox',
                                        'Planning': 'fas fa-clipboard-list',
                                        'In Progress': 'fas fa-spinner',
                                        'Completed': 'fas fa-check-circle',
                                        'On Hold': 'fas fa-pause-circle',
                                        'Cancelled': 'fas fa-times-circle'
                                    };
                                    const statusLabels = {
                                        'Backlog': 'Pending review',
                                        'Planning': 'Being planned',
                                        'In Progress': 'Active work',
                                        'Completed': 'Successfully done',
                                        'On Hold': 'Temporarily paused',
                                        'Cancelled': 'Not proceeding'
                                    };
                                    return (
                                        <Col xs={6} md={4} key={index}>
                                            <div 
                                                className="border rounded-3 h-100 p-3 status-card-hover"
                                                style={{ 
                                                    backgroundColor: `${stat.color}08`,
                                                    borderColor: `${stat.color}30`
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between mb-3">
                                                    <div 
                                                        className="status-icon-box d-flex align-items-center justify-content-center"
                                                        style={{ 
                                                            backgroundColor: `${stat.color}20`,
                                                            color: stat.color
                                                        }}
                                                    >
                                                        <i className={statusIcons[stat.status]}></i>
                                                    </div>
                                                    <div className="fw-bold fs-28" style={{ color: stat.color }}>
                                                        {stat.count}
                                                    </div>
                                                </div>
                                                <div className="fw-semibold text-dark mb-1">{stat.status}</div>
                                                <div className="text-muted small">{statusLabels[stat.status]}</div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </div>
                </Col>

                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <span>Upcoming Deadlines</span>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="deadline-scroll-container custom-scrollbar d-flex flex-column gap-3">
                                {upcomingDeadlines.map((item, index) => (
                                    <div 
                                        key={index} 
                                        className="p-3 border rounded-3 deadline-card-hover cursor-pointer"
                                        onClick={() => handleViewTaskDetails(item)}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="fw-semibold flex-grow-1 lh-base small">
                                                {item.task}
                                            </div>
                                            <span 
                                                className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-xs modern-badge badge-${getUrgencyColor(item.urgency)} ms-2`}
                                            >
                                                {item.urgency.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                            <div>
                                                <div className="text-muted text-uppercase info-label">Due Date</div>
                                                <div className="fw-semibold small">
                                                    {item.dueDate}
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className={`fw-bold fs-5 text-${getUrgencyColor(item.urgency)}`}>
                                                    {item.daysLeft}
                                                </div>
                                                <small className="text-muted">days left</small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ============================================ */}
            {/* 5. TASKS BY RESOURCE - TEAM PERFORMANCE & PROJECT TIMELINE */}
            {/* ============================================ */}
            <Row className="g-4 mb-4">
                <Col lg={8}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div>
                                <div className="fw-bold">Tasks by Resource</div>
                            </div>
                        </div>
                        <div className='c-card-body-1 p-0'>
                            <Table responsive className='c-table'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th width="120px" className='text-center'>Role</th>
                                <th width="100px" className='text-center'>Overdue / Open</th>
                                <th width="100px" className='text-center'>Completed</th>
                                <th width="100px" className='text-center'>Total</th>
                                <th width="180px" className='text-center'>Tasks Progress</th>
                                <th width="100px" className='text-center'>Efficiency</th>
                                <th width="80px" className='text-center'>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {teamPerformance
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((member) => (
                                <tr key={member.id}>
                                    <td>
                                        <div className='d-flex align-items-center gap-3'>
                                            {member.avatar ? (
                                                <img src={member.avatar} alt={member.name} className="avatar-40 rounded-circle" />
                                            ) : (
                                                <div className="avatar-40 rounded-circle avatar-gradient d-flex align-items-center justify-content-center">
                                                    {member.initials}
                                                </div>
                                            )}
                                            <div>
                                                <div className='fw-bold'>{member.name}</div>
                                                <div className='text-muted small'>{member.role}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className='text-center'>{member.role}</td>
                                    <td className='text-center'>
                                        <span className='fw-bold text-danger'>
                                            {member.tasksPending > 0 ? '1' : '0'} / {member.tasksPending}
                                        </span>
                                    </td>
                                    <td className='text-center'>
                                        <span className='fw-bold text-success'>{member.tasksCompleted}</span>
                                    </td>
                                    <td className='text-center'>
                                        <span className='fw-bold text-primary'>{member.tasksAssigned}</span>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column">
                                            <div className="fw-bold small mb-1">
                                                {Math.round((member.tasksCompleted / member.tasksAssigned) * 100)}%
                                            </div>
                                            <div className="progress progress-h-8 mb-1">
                                                <div 
                                                    className="progress-bar progress-gradient-green" 
                                                    style={{ width: `${(member.tasksCompleted / member.tasksAssigned) * 100}%` }}
                                                ></div>
                                            </div>
                                            <div className="small text-muted d-flex justify-content-between">
                                                <span>{member.tasksCompleted} Completed</span>
                                                <span>{member.tasksAssigned} Total</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className='text-center'>
                                        <span className={`fw-bold ${member.efficiency >= 90 ? 'text-success' : member.efficiency >= 75 ? 'text-warning' : 'text-danger'}`}>
                                            {member.efficiency}%
                                        </span>
                                    </td>
                                    <td className='text-center'>
                                        <Button 
                                            variant="link" 
                                            className="p-0 text-primary"
                                            onClick={() => handleViewMemberDetails(member)}
                                        >
                                            <i className="fas fa-eye"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <div className='c-card-footer-1'>
                    <div className='d-flex justify-content-between align-items-center c-pagination flex-wrap'>
                        <div className="p-tag d-flex align-items-center text-nowrap">
                            Show
                            <select 
                                className="form-select mx-2 min-w-70"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                            </select>
                            per page ({teamPerformance.length} total)
                        </div>
                        <div className='d-flex align-items-center gap-2 p-tag'>
                            <div className='text-nowrap'>
                                {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, teamPerformance.length)} of {teamPerformance.length} Records
                            </div>
                            <select 
                                className='form-select min-w-70'
                                value={currentPage}
                                onChange={(e) => setCurrentPage(Number(e.target.value))}
                            >
                                {Array.from({ length: Math.ceil(teamPerformance.length / itemsPerPage) }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                            <Button 
                                variant="btn"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(currentPage - 1)}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </Button>
                            <Button 
                                variant="btn"
                                disabled={currentPage === Math.ceil(teamPerformance.length / itemsPerPage)}
                                onClick={() => setCurrentPage(currentPage + 1)}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </Button>
                        </div>
                    </div>
                </div>
                    </div>
                </Col>

                <Col lg={4}>
                    <div className='c-card-1 h-100'>
                        <div className='c-card-header-1'>
                            <div>
                                <div className="fw-bold">Project Timeline</div>
                            </div>
                        </div>
                        <div className='c-card-body-1'>
                            <div className="timeline-redesign timeline-scroll-container custom-scrollbar">
                                {milestones.map((milestone, index) => {
                                    const milestoneIcons = {
                                        'Project Kickoff': 'fa-rocket',
                                        'Design Phase': 'fa-pencil-ruler',
                                        'Development Phase': 'fa-code',
                                        'Testing Phase': 'fa-vial',
                                        'Deployment': 'fa-upload'
                                    };
                                    return (
                                        <div key={index} className={`timeline-milestone-card ${index < milestones.length - 1 ? 'mb-3' : ''}`}>
                                            <div className="d-flex align-items-center gap-3 p-3 border rounded-3 bg-white">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                    style={{ 
                                                        width: '48px', 
                                                        height: '48px',
                                                        backgroundColor: `${milestone.color}20`,
                                                        color: milestone.color
                                                    }}
                                                >
                                                    <i className={`fas ${milestoneIcons[milestone.name]}`}></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="mb-0 fw-semibold small">{milestone.name}</h6>
                                                        <span 
                                                            className="fw-semibold small"
                                                            style={{ color: milestone.color }}
                                                        >
                                                            {milestone.duration}%
                                                        </span>
                                                    </div>
                                                    <div className="position-relative">
                                                        <div className="rounded-pill bg-light overflow-hidden progress-h-8">
                                                            <div 
                                                                className="rounded-pill h-100"
                                                                style={{
                                                                    width: `${milestone.duration}%`,
                                                                    backgroundColor: milestone.color
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ============================================ */}
            {/* 6. TIME LOGGED - MERGED TABLE */}
            {/* ============================================ */}
            <div className='c-card-1 mb-4'>
                <div className='c-card-header-1'>
                    <div>
                        <div className="fw-bold">Time Logged - Resource & Timesheets</div>
                    </div>
                </div>
                <div className='c-card-body-1 p-0'>
                    <Table responsive className="c-table mb-0">
                        <thead>
                            <tr>
                                <th>Resource Name</th>
                                <th>Task Name</th>
                                <th className="text-center">Date</th>
                                <th className="text-center">Start Time</th>
                                <th className="text-center">End Time</th>
                                <th className="text-center">Total Hours</th>
                                <th>Notes</th>
                                <th className="text-center">Billable</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {timeLogData.map((log) => (
                                <tr key={log.id} className="timelog-row">
                                    <td>
                                        <span className="fw-semibold">{log.resourceName}</span>
                                    </td>
                                    <td>{log.taskName}</td>
                                    <td className="text-center">{log.date}</td>
                                    <td className="text-center">{log.startTime}</td>
                                    <td className="text-center">{log.endTime}</td>
                                    <td className="text-center">
                                        <span className="fw-semibold text-info">{log.totalHours}</span>
                                    </td>
                                    <td>
                                        <small className="text-muted">{log.notes}</small>
                                    </td>
                                    <td className="text-center">
                                        {log.billable ? (
                                            <span className="fw-semibold text-success">Billable</span>
                                        ) : (
                                            <span className="fw-semibold text-secondary">Non-Billable</span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <span className={`fw-semibold ${log.status === 'Approved' ? 'text-success' : 'text-warning'}`}>
                                            {log.status}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="p-0"
                                            onClick={() => handleViewTimeLog(log)}
                                        >
                                            <i className="fas fa-eye"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* ============================================ */}
            {/* MODALS & OFFCANVAS */}
            {/* ============================================ */}
            <FilterProjectReportsOffcanvas
                show={showFilter}
                onHide={() => setShowFilter(false)}
                teamMemberFilter={teamMemberFilter}
                setTeamMemberFilter={setTeamMemberFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                workflowFilter={workflowFilter}
                setWorkflowFilter={setWorkflowFilter}
                dateRangeFilter={dateRangeFilter}
                setDateRangeFilter={setDateRangeFilter}
                teamMemberOptions={teamMemberOptions}
                statusOptions={statusOptions}
                priorityOptions={priorityOptions}
                workflowOptions={workflowOptions}
                onApply={() => {
                    console.log('Filters applied:', { 
                        teamMemberFilter, 
                        statusFilter, 
                        priorityFilter, 
                        workflowFilter, 
                        dateRangeFilter 
                    });
                    setShowFilter(false);
                }}
                onClear={() => {
                    setTeamMemberFilter([]);
                    setStatusFilter(null);
                    setPriorityFilter(null);
                    setWorkflowFilter([]);
                    setDateRangeFilter([null, null]);
                }}
            />

            <ExportReportModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                onExport={handleExport}
            />

            <ViewTaskDetailsModal
                show={showTaskModal}
                onHide={() => setShowTaskModal(false)}
                task={selectedItem}
            />

            <ViewResourceDetailsModal
                show={showResourceModal}
                onHide={() => setShowResourceModal(false)}
                resource={selectedItem}
            />

            <ViewTimeLogDetailsModal
                show={showTimeLogModal}
                onHide={() => setShowTimeLogModal(false)}
                timeLog={selectedItem}
            />
        </>
    );
};

export default ProjectReport;
